import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, ArrowUpRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import CTASection from "@/sections/CTASection";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SERVICES } from "@/data/services";
import { PROJECTS } from "@/data/projects";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);

  if (!service) return <Navigate to="/services" replace />;

  const Icon = service.icon;
  const related = PROJECTS.filter((p) =>
    p.services.some((sv) => service.title.toLowerCase().includes(sv.toLowerCase().split(" ")[0]))
  ).slice(0, 3);

  return (
    <div data-testid="service-detail-page">
      <PageHero
        overline="Service"
        title={service.title}
        subtitle={service.short}
        image={service.hero}
        breadcrumbs={[{ label: "Services", to: "/services" }, { label: service.title }]}
      />

      <section className="bg-white py-24 sm:py-32">
        <div className="container-x grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-4 lg:sticky lg:top-32 self-start">
            <div className="w-16 h-16 grid place-items-center bg-navy text-gold">
              <Icon size={28} strokeWidth={1.5} />
            </div>
            <div className="overline text-emerald-brand mt-6">Overview</div>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-navy mt-4 leading-tight">
              {service.title}
            </h2>
            <p className="mt-6 text-base text-slate-brand leading-relaxed">{service.short}</p>
            <Link
              to="/contact"
              data-testid="service-detail-cta"
              className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-gold text-navy font-semibold text-xs tracking-[0.2em] uppercase hover:bg-[#E8C45F] transition-colors"
            >
              Discuss your project <ArrowRight size={14} />
            </Link>
          </div>

          <div className="lg:col-span-8 space-y-14">
            <div>
              <div className="overline text-emerald-brand">Scope of work</div>
              <h3 className="font-display font-bold text-2xl text-navy mt-2">What we deliver</h3>
              <ul className="mt-6 space-y-3">
                {service.scope.map((s) => (
                  <li key={s} className="flex items-start gap-3 text-slate-brand">
                    <CheckCircle2 className="text-emerald-brand shrink-0 mt-0.5" size={18} />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="overline text-emerald-brand">Workflow</div>
              <h3 className="font-display font-bold text-2xl text-navy mt-2">How we engage</h3>
              <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
                {service.workflow.map((w, i) => (
                  <motion.div
                    key={w}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.05 }}
                    className="p-4 border border-slate-200"
                  >
                    <div className="font-mono-alt text-xs text-gold">0{i + 1}</div>
                    <div className="mt-2 font-display font-semibold text-sm text-navy">{w}</div>
                  </motion.div>
                ))}
              </div>
            </div>

            <div>
              <div className="overline text-emerald-brand">Benefits</div>
              <h3 className="font-display font-bold text-2xl text-navy mt-2">Why clients choose us</h3>
              <div className="mt-6 grid sm:grid-cols-2 gap-3">
                {service.benefits.map((b) => (
                  <div key={b} className="p-5 bg-mist border-l-2 border-gold text-sm text-slate-brand">
                    {b}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="overline text-emerald-brand">Industries served</div>
              <div className="mt-4 flex flex-wrap gap-2">
                {service.industries.map((ind) => (
                  <span key={ind} className="px-4 py-2 border border-slate-300 text-sm text-slate-700">
                    {ind}
                  </span>
                ))}
              </div>
            </div>

            {service.faqs?.length > 0 && (
              <div>
                <div className="overline text-emerald-brand">FAQs</div>
                <h3 className="font-display font-bold text-2xl text-navy mt-2">Common questions</h3>
                <Accordion type="single" collapsible className="mt-6">
                  {service.faqs.map((f, i) => (
                    <AccordionItem key={i} value={`faq-${i}`} data-testid={`service-faq-${i}`}>
                      <AccordionTrigger className="text-left font-display font-semibold text-navy hover:text-emerald-brand">
                        {f.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-slate-brand">{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-mist py-24">
          <div className="container-x">
            <div className="overline text-emerald-brand">Related projects</div>
            <h3 className="font-display font-extrabold text-3xl sm:text-4xl text-navy mt-2">
              See it in practice.
            </h3>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to="/projects"
                  className="group relative aspect-[4/3] overflow-hidden bg-navy"
                  data-testid={`related-project-${p.slug}`}
                >
                  <img src={p.cover} alt={p.title} className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/30 to-transparent" />
                  <div className="absolute bottom-0 inset-x-0 p-5 text-white">
                    <span className="overline text-gold">{p.category}</span>
                    <div className="mt-1 font-display font-bold text-lg">{p.title}</div>
                  </div>
                  <ArrowUpRight className="absolute top-5 right-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" size={20} />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTASection />
    </div>
  );
}
